import React, { useEffect, useMemo } from 'react';
import { useForm, useWatch, Controller } from 'react-hook-form';
import { Bill } from '../../types';
import Input from './Input';
import Select from './Select';
import AsyncSelect from './AsyncSelect';
import TextArea from './TextArea';
import Button from './Button';
import { providersService, categoriesService } from '../../services';
import { formatDateForAPI, formatDateForInput } from '../../utils/dateFormatter';
import { useCurrencies } from '../../hooks/useCurrencies';
import { resolveCurrencyDocumentId } from '../../utils/currencyUtils';

interface BillFormProps {
  bill?: Bill;
  onSubmit: (data: Partial<Bill>) => void;
  onCancel: () => void;
}

const BillForm: React.FC<BillFormProps> = ({ bill, onSubmit, onCancel }) => {
  // Fetch currencies from API
  const { data: currencies } = useCurrencies();

  // Saved currency may be a populated object, a documentId, or a legacy code
  const savedCurrencyId = bill ? resolveCurrencyDocumentId(bill.currency, currencies) : '';

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
    control
  } = useForm<Bill>({
    defaultValues: bill ? {
      ...bill,
      dueDate: formatDateForInput(bill.dueDate),
      currency: savedCurrencyId as any
    } : {
      providerId: '',
      amount: 0,
      currency: '',
      exchangeRate: undefined,
      dueDate: '',
      status: 'pending',
      category: '',
      description: '',
      deductionAmount: undefined,
      deductionDescription: ''
    }
  });

  // Re-apply the saved currency once the async options are loaded (edit case)
  useEffect(() => {
    if (savedCurrencyId && currencies && currencies.length > 0) {
      setValue('currency', savedCurrencyId as any);
    }
  }, [savedCurrencyId, currencies, setValue]);

  // Watch the currency field to conditionally show exchange rate
  const selectedCurrency = useWatch({
    control,
    name: 'currency',
    defaultValue: ''
  });

  // Currency option values are documentIds; resolve the code for the USD checks
  const selectedCurrencyCode = useMemo(() => {
    const c: any = (currencies as any[] | undefined)?.find(
      (cur: any) => (cur.documentId || String(cur.id)) === selectedCurrency || cur.code === selectedCurrency
    );
    return c?.code as string | undefined;
  }, [currencies, selectedCurrency]);

  const handleFormSubmit = (data: Bill) => {
    // If currency is EGP, remove exchange rate
    const submitData: any = {
      ...data,
      dueDate: formatDateForAPI(data.dueDate),
      deductionAmount: data.deductionAmount ? Number(data.deductionAmount) : undefined,
      exchangeRate: selectedCurrencyCode === 'USD' ? data.exchangeRate : undefined
    };
    // The original amount is written once on create and must never change,
    // even when the bill amount is edited later
    if (bill) {
      delete submitData.originalAmount;
    } else {
      submitData.originalAmount = Number(data.amount) || 0;
    }

    onSubmit({
      ...submitData,
      ...(bill ? { id: bill.id, documentId: bill.documentId } : {}),
      createdAt: bill?.createdAt || new Date().toISOString()
    });
  };

  // Fetch providers with search and pagination
  const fetchProviders = async (query: string, page: number) => {
    const response = await providersService.getAll({
      filters: query ? { name: { $containsi: query } } : undefined,
      pagination: { page, pageSize: 20 },
      sort: ['name:asc']
    });
    return response;
  };

  // Fetch categories with search and pagination
  const fetchCategories = async (query: string, page: number) => {
    return categoriesService.getAll({
      filters: query ? { name: { $containsi: query } } : undefined,
      pagination: { page, pageSize: 20 },
      sort: ['name:asc']
    }) as any;
  };

  // Currency options from API - NO HARDCODED VALUES
  const currencyOptions = useMemo(() => {
    if (!currencies || currencies.length === 0) {
      return [];
    }
    return currencies
      .filter((currency: any) => currency.isActive !== false)
      .map((currency: any) => ({
        value: currency.documentId || currency.id || '',
        label: `${currency.code || ''} (${currency.name || currency.code || 'Unknown'})`
      }));
  }, [currencies]);

  const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'paid', label: 'Paid' },
    { value: 'overdue', label: 'Overdue' }
  ];

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h2 className="text-xl font-semibold text-gray-900 mb-6">
        {bill ? 'Edit Bill' : 'Add New Bill'}
      </h2>

      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Controller
            name="providerId"
            control={control}
            rules={{ required: 'Provider is required' }}
            render={({ field }) => (
              <AsyncSelect
                label="Provider"
                name="providerId"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={errors.providerId?.message as string}
                required
                placeholder="Select provider..."
                fetchOptions={fetchProviders}
                getOptionLabel={(provider) => provider.name}
                getOptionValue={(provider) => provider.documentId || provider.id?.toString()}
              />
            )}
          />

          {/* The bill "category" field is a plain string, so the option value is the category name */}
          <Controller
            name="category"
            control={control}
            rules={{ required: 'Category is required' }}
            render={({ field }) => (
              <AsyncSelect
                label="Category"
                name="category"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={errors.category?.message as string}
                required
                placeholder="Select category..."
                fetchOptions={fetchCategories}
                getOptionLabel={(cat) => `${cat.name || ''}${cat.type ? ` (${cat.type})` : ''}`}
                getOptionValue={(cat) => cat.name || ''}
              />
            )}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Select
            label="Currency"
            {...register('currency', { required: 'Currency is required' })}
            options={currencyOptions}
            placeholder="Select currency"
            error={errors.currency?.message}
          />

          <Input
            label="Amount"
            type="number"
            step="0.01"
            {...register('amount', { 
              required: 'Amount is required',
              min: { value: 0.01, message: 'Amount must be greater than 0' }
            })}
            error={errors.amount?.message}
            placeholder="0.00"
          />
        </div>

        {/* Original amount is fixed at creation and can never be edited */}
        {bill && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="Original Amount"
              type="number"
              value={bill.originalAmount ?? bill.amount ?? 0}
              disabled
              readOnly
              helperText="The bill's original price — set when the bill was created and never changes"
            />
            <div></div>
          </div>
        )}

        {/* Conditionally show exchange rate field only for USD */}
        {selectedCurrencyCode === 'USD' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="Exchange Rate (USD to EGP)"
              type="number"
              step="0.01"
              {...register('exchangeRate', { 
                required: selectedCurrencyCode === 'USD' ? 'Exchange rate is required for USD' : false,
                min: { value: 0.01, message: 'Exchange rate must be greater than 0' }
              })}
              error={errors.exchangeRate?.message}
              placeholder="e.g., 30.50"
            />
            <div></div> {/* Empty div for grid alignment */}
          </div>
        )}

        {/* Deduction */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Amount To Be Deducted"
            type="number"
            step="0.01"
            {...register('deductionAmount', {
              min: { value: 0, message: 'Deduction cannot be negative' }
            })}
            error={errors.deductionAmount?.message}
            placeholder="0.00"
          />
          <Input
            label="Deduction Description"
            {...register('deductionDescription')}
            error={errors.deductionDescription?.message}
            placeholder="Reason for the deduction (optional)"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Due Date"
            type="date"
            {...register('dueDate', { required: 'Due date is required' })}
            error={errors.dueDate?.message}
          />

          <Select
            label="Status"
            {...register('status', { required: 'Status is required' })}
            options={statusOptions}
            error={errors.status?.message}
          />
        </div>

        <TextArea
          label="Description"
          {...register('description', {
            required: 'Description is required',
            minLength: { value: 10, message: 'Description must be at least 10 characters' }
          })}
          error={errors.description?.message}
          placeholder="Enter bill description or details"
          rows={3}
        />

        {/* Display calculated EGP amount if USD is selected */}
        {selectedCurrencyCode === 'USD' && (
          <div className="bg-blue-50 p-4 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> If you enter both amount and exchange rate, the EGP equivalent will be calculated automatically.
            </p>
          </div>
        )}

        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={isSubmitting}
          >
            {bill ? 'Update Bill' : 'Create Bill'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default BillForm;